import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';

// Client data layer for the Task detail's "Files" section: one fetch to /api/task-files?taskId=
// returns the files that task produced, listed off the file-system by `origin = {AGENT_TASK, taskId}`.
// orion-web's CLIENT bundle must not import common-lib, so the item shape is a client-local contract
// matching the route's `TaskFileItem`.

/** One file a task produced. `content` arrives WITH the list, so opening it needs no second fetch. */
export interface TaskFile {
  name: string;
  /** The directory the file lives in. A path is only unique WITHIN one, so the list is keyed on both. */
  directory: string;
  path: string;
  content: string;
  extension?: string;
  contentType?: string;
  referenceUrl?: string;
}

/** The list key: a path is unique only within its directory, so a task producing `report.md` in two
 *  directories yields two items React must be able to tell apart. */
export function taskFileKey(file: TaskFile): string {
  return `${file.directory}/${file.path}`;
}

export async function fetchTaskFiles(taskId: string): Promise<TaskFile[]> {
  const res = await apiFetch(`${API_BASE}/api/task-files?taskId=${encodeURIComponent(taskId)}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { items?: TaskFile[] };
  return data.items ?? [];
}

/** The task's produced files, or an empty list while loading / on failure — the Files section is a
 *  decoration on the detail, so it renders nothing rather than an error state of its own. */
export function useTaskFiles(taskId: string | undefined): TaskFile[] {
  const query = useQuery({
    queryKey: queryKeys.taskFiles(taskId),
    queryFn: () => fetchTaskFiles(taskId as string),
    enabled: !!taskId,
  });

  // A file produced while the detail is open lands in the list live — the same trigger the workspace
  // Files surfaces use.
  useFileSystemDuplexer(() => {
    if (taskId) void query.refetch();
  });

  return query.data ?? [];
}
